import React from 'react';

import { Link, useLocation, useNavigate } from 'react-router-dom';

/*
  Floating launcher for the full-page AI Shopping Assistant. Sits just
  above the Help chat bubble (bottom-right). Clicking navigates to the
  /assistant page. While already on that page it turns into a close (✕)
  button that takes you back where you came from.
*/

/* Shared innards for both the link and the close-button variant, so the
   halo/spark decoration only exists in one place. */
function LauncherFace({ icon, label }) {
  return (
    <>
      <span className="assistant-launcher-orb">
        {/* Halo rings radiating outward — the genie's aura, and what stops
            the button from disappearing into the corner. */}
        <span className="assistant-halo" />
        <span className="assistant-halo assistant-halo-2" />

        <span className="assistant-launcher-icon">
          <span className="assistant-icon-face">{icon}</span>
        </span>

        <span className="assistant-spark assistant-spark-1">✦</span>
        <span className="assistant-spark assistant-spark-2">✧</span>
        <span className="assistant-spark assistant-spark-3">✦</span>
      </span>

      <span className="assistant-launcher-name">{label}</span>
    </>
  );
}

function AssistantLauncher() {
  const location = useLocation();
  const navigate = useNavigate();

  const onAssistant = location.pathname === '/assistant';

  return (
    <>
      {onAssistant ? (
        <button
          type="button"
          className="assistant-launcher assistant-launcher-close"
          onClick={() => navigate(-1)}
          aria-label="Close AI Shopping Assistant"
        >
          <LauncherFace icon="✕" label="Close" />
        </button>
      ) : (
        <Link
          to="/assistant"
          className="assistant-launcher"
          aria-label="Open AI Shopping Assistant"
        >
          <LauncherFace icon="🧞" label="ShopGenie" />
        </Link>
      )}

      <style>{launcherStyles}</style>
    </>
  );
}

const launcherStyles = `
  /* Fixed 60px box so the circle lines up exactly above the Help circle.
     Raised enough that its label clears the Help button below it.
     The box itself never moves — only the orb inside floats, so the label
     and the alignment with the Help button below stay put. */
  .assistant-launcher {
    position: fixed;
    bottom: 150px;
    right: 22px;
    width: 60px;
    height: 60px;
    display: block;
    text-decoration: none;
    z-index: 1050;
    /* reset so it also works as a <button> */
    border: none;
    background: transparent;
    padding: 0;
    cursor: pointer;
  }
  /* On mobile the speed-dial (MobileFabDial) replaces this launcher */
  @media (max-width: 1199.98px) {
    .assistant-launcher { display: none !important; }
  }

  .assistant-launcher-orb {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    animation: assistantFloat 4.2s ease-in-out infinite;
  }
  @keyframes assistantFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-5px); }
  }

  /* Expanding aura rings */
  .assistant-halo {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    border: 2px solid rgba(139, 92, 246, 0.6);
    opacity: 0;
    pointer-events: none;
    animation: assistantHalo 3s ease-out infinite;
  }
  .assistant-halo-2 {
    border-color: rgba(245, 158, 11, 0.55);
    animation-delay: 1.5s;
  }
  @keyframes assistantHalo {
    0%   { opacity: 0.85; transform: scale(1); border-width: 2px; }
    70%  { opacity: 0.12; }
    100% { opacity: 0; transform: scale(1.9); border-width: 0.5px; }
  }

  .assistant-launcher-icon {
    position: relative;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    /* Divine violet → saffron with a golden halo around the genie */
    background:
      radial-gradient(circle at 50% 38%, rgba(253,230,138,0.55), transparent 62%),
      linear-gradient(135deg, #6d28d9, #ea580c);
    background-size: 160% 160%;
    border: 2px solid rgba(253,230,138,0.75);
    color: #fff;
    font-size: 28px;
    display: grid;
    place-items: center;
    overflow: hidden;
    box-shadow: 0 10px 26px rgba(124, 58, 237, 0.45), 0 0 16px rgba(245,158,11,0.4);
    transition: transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
                box-shadow 0.25s ease, background-position 0.4s ease;
  }
  /* Light sweeping across the orb */
  .assistant-launcher-icon::after {
    content: '';
    position: absolute;
    top: -60%;
    left: -120%;
    width: 55%;
    height: 220%;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.45), transparent);
    transform: skewX(-20deg);
    animation: assistantSheen 5s ease-in-out infinite;
  }
  @keyframes assistantSheen {
    0%, 60%  { left: -120%; }
    82%      { left: 170%; }
    100%     { left: 170%; }
  }

  /* The genie breathes and tilts while it waits */
  .assistant-icon-face {
    position: relative;
    z-index: 1;
    display: block;
    line-height: 1;
    animation: assistantGenie 5.5s ease-in-out infinite;
  }
  @keyframes assistantGenie {
    0%, 100% { transform: translateY(0) rotate(0deg) scale(1); }
    25%      { transform: translateY(-2px) rotate(-8deg) scale(1.06); }
    60%      { transform: translateY(1px) rotate(7deg) scale(1); }
  }

  /* Sparks drifting off the orb */
  .assistant-spark {
    position: absolute;
    font-size: 11px;
    color: #fde68a;
    opacity: 0;
    pointer-events: none;
    text-shadow: 0 0 6px rgba(253, 224, 71, 0.9);
    animation: assistantSpark 3.4s ease-out infinite;
  }
  .assistant-spark-1 { top: 0;    right: 2px; animation-delay: 0.3s; }
  .assistant-spark-2 { bottom: 4px; left: 0;  animation-delay: 1.5s; font-size: 9px; }
  .assistant-spark-3 { top: 8px;  left: 2px;  animation-delay: 2.4s; font-size: 8px; }
  @keyframes assistantSpark {
    0%   { opacity: 0; transform: translate(0,0) scale(0.4) rotate(0deg); }
    18%  { opacity: 1; transform: translate(5px,-7px) scale(1) rotate(45deg); }
    55%  { opacity: 0.7; }
    100% { opacity: 0; transform: translate(12px,-20px) scale(0.3) rotate(190deg); }
  }

  .assistant-launcher:hover .assistant-launcher-icon {
    transform: scale(1.12);
    background-position: right center;
    box-shadow: 0 14px 34px rgba(124, 58, 237, 0.55), 0 0 28px rgba(245,158,11,0.7);
  }
  .assistant-launcher:active .assistant-launcher-icon {
    transform: scale(0.95);
  }

  /* Close variant shown while on the assistant page — deliberately calmer,
     it is an exit, not an invitation. */
  .assistant-launcher-close .assistant-launcher-icon {
    background: linear-gradient(135deg, #6b7280, #374151);
    font-size: 24px;
    box-shadow: 0 10px 26px rgba(55, 65, 81, 0.45);
  }
  .assistant-launcher-close .assistant-launcher-name {
    color: #374151;
  }
  .assistant-launcher-close .assistant-halo,
  .assistant-launcher-close .assistant-spark,
  .assistant-launcher-close .assistant-launcher-icon::after {
    display: none;
  }
  .assistant-launcher-close .assistant-icon-face {
    animation: none;
  }
  .assistant-launcher-close:hover .assistant-icon-face {
    transform: rotate(90deg);
    transition: transform 0.25s ease;
  }

  /* Centered under the circle without shifting it */
  .assistant-launcher-name {
    position: absolute;
    top: 65px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.3px;
    color: #6d28d9;
    background: #fff;
    padding: 2px 10px;
    border-radius: 12px;
    box-shadow: 0 4px 10px rgba(124, 58, 237, 0.25);
    white-space: nowrap;
    transition: background 0.2s ease, box-shadow 0.2s ease;
  }
  .assistant-launcher:hover .assistant-launcher-name {
    background: #faf5ff;
    box-shadow: 0 6px 16px rgba(124, 58, 237, 0.35);
  }

  @media (prefers-reduced-motion: reduce) {
    .assistant-launcher-orb,
    .assistant-icon-face,
    .assistant-launcher-icon::after {
      animation: none !important;
    }
    .assistant-halo, .assistant-spark { display: none; }
  }
`;

export default AssistantLauncher;
